import type { Agent } from '@/api/agent/types';

/** 专家页签：全部 / 收藏（收藏在前端按 isFavorite 过滤） */
export type ExpertCategoryTab = 'all' | 'favorite';

/** 专家列表排序：创建时间(默认) / 使用时间 / 使用次数 */
export type ExpertSortBy = 'createTime' | 'lastChatTime' | 'usageCount';

export const DEFAULT_EXPERT_TAB: ExpertCategoryTab = 'all';

export const DEFAULT_EXPERT_SORT: ExpertSortBy = 'createTime';

export const PLATFORM_EXPERT_TABS: ExpertCategoryTab[] = ['all', 'favorite'];

export const PLATFORM_EXPERT_SORTS: ExpertSortBy[] = ['createTime', 'lastChatTime', 'usageCount'];

function filterChatAgents(agents: Agent[]) {
  if (!agents?.length)
    return [];
  return agents.filter(agent => agent.agentSource !== 'third_party');
}

/** 第三方嵌入智能体（小龙虾） */
export function resolveThirdPartyAgent(agents: Agent[]): Agent | null {
  if (!agents?.length)
    return null;
  return agents.find(
    agent => String(agent.agentSource || '').toLowerCase() === 'third_party',
  ) || null;
}

/** 专家智能体 = 可对话智能体中排除第三方嵌入型 */
export function resolveExpertAgents(agents: Agent[]): Agent[] {
  return filterChatAgents(agents);
}

export function isExpertAgent(
  agentId: string | number | undefined | null,
  agents: Agent[],
): boolean {
  if (agentId === undefined || agentId === null || agentId === '')
    return false;
  const expertIds = new Set(resolveExpertAgents(agents).map(agent => String(agent.id)));
  return expertIds.has(String(agentId));
}

export function getExpertChatPath(agentId: string | number, conversationId = 'new') {
  return `/experts/${agentId}/chat/${conversationId}`;
}

export function getAssistantChatPath(agentId: string | number, conversationId = 'new') {
  return `/chat/${agentId}/${conversationId}`;
}

/** 助理模块默认聊天路径（优先小龙虾） */
export function getDefaultAssistantChatPath(agents: Agent[], conversationId = 'new') {
  const agent = resolveThirdPartyAgent(agents);
  if (!agent)
    return '/chat';
  return getAssistantChatPath(agent.id, conversationId);
}

export function formatExpertUses(count?: number) {
  const value = Number(count || 0);
  if (value >= 1000)
    return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(value);
}

/** 从接口原始字段解析专家评分 */
export function parseAgentRatingFromPayload(source: Record<string, unknown> | null | undefined): number | undefined {
  if (!source)
    return undefined;
  const value = Number(source.rating ?? source.ratingScore ?? source.avgRating ?? source.averageRating ?? source.score ?? 0);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

export function getExpertUsageCount(agent: Agent) {
  return agent.instantChatCount || agent.personalConversationCount || 0;
}

/** 专家卡片点赞总数（workbench likeCount） */
export function getExpertLikeCount(agent: Agent) {
  const value = Number(agent.likeCount ?? 0);
  return Number.isFinite(value) && value >= 0 ? value : 0;
}

/** 当前用户是否已收藏（workbench isFavorite） */
export function isExpertFavorite(agent: Agent) {
  return Boolean(agent.isFavorite);
}

/** 当前用户是否已评价（workbench hasEvaluated） */
export function hasExpertEvaluated(agent: Agent) {
  return Boolean(agent.hasEvaluated);
}

function parseTimeMs(value?: string) {
  if (!value)
    return Number.NaN;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : Number.NaN;
}

/** 专家列表排序（降序；缺省值排后） */
export function sortExpertAgents(agents: Agent[], sortBy: ExpertSortBy = DEFAULT_EXPERT_SORT): Agent[] {
  const list = [...agents];
  list.sort((a, b) => {
    if (sortBy === 'usageCount') {
      const diff = getExpertUsageCount(b) - getExpertUsageCount(a);
      if (diff !== 0)
        return diff;
    }
    else {
      const aTime = parseTimeMs(sortBy === 'lastChatTime' ? a.lastChatTime : a.createTime);
      const bTime = parseTimeMs(sortBy === 'lastChatTime' ? b.lastChatTime : b.createTime);
      const aValid = Number.isFinite(aTime);
      const bValid = Number.isFinite(bTime);
      if (aValid && bValid) {
        const diff = bTime - aTime;
        if (diff !== 0)
          return diff;
      }
      else if (aValid !== bValid) {
        return aValid ? -1 : 1;
      }
    }

    return String(a.id).localeCompare(String(b.id));
  });
  return list;
}
